import dayjs from 'dayjs'
import { sortBy } from './collection'

export interface TimeRange {
  /** ISO-8601 UTC start of the range. */
  start: string
  /** ISO-8601 UTC end of the range. */
  end: string
}

// Convert a PostgreSQL timestamptz text value — Postgres' canonical
// DateStyle=ISO output, e.g. "2026-06-16 10:30:00+00" — into a strict ISO-8601
// string that every JS engine parses, including Safari/iOS. Postgres uses a
// space separator and a minutes-less offset like "+00"/"+03"; neither is valid
// ISO-8601, and Safari's Date parser rejects both (returning Invalid Date),
// while V8/Node accept them — which is why naive `new Date(...)`/`dayjs(...)`
// parsing worked in CI/Chrome but crashed on real iPhones.
function pgTimestampToIso(value: string): string | null {
  const match = value
    .trim()
    .match(
      /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2}(?:\.\d+)?)(Z|[+-]\d{2}(?::?\d{2})?)?$/,
    )
  if (!match) return null
  const [, date, time, rawOffset] = match
  return `${date}T${time}${normalizeOffset(rawOffset)}`
}

// Normalize a Postgres offset ("+00", "+03", "+0530", "+05:30", "Z" or absent)
// to a canonical "[+-]HH:MM" / "Z" suffix.
function normalizeOffset(raw: string | undefined): string {
  if (!raw || raw === 'Z') return 'Z'
  const sign = raw[0]
  const digits = raw.slice(1).replace(':', '')
  const hours = digits.slice(0, 2)
  const minutes = digits.slice(2, 4) || '00'
  return `${sign}${hours}:${minutes}`
}

// Parse a PostgreSQL tstzrange literal — e.g.
// ["2026-06-16 10:30:00+00","2026-06-16 14:00:00+00") — into ISO-8601 UTC
// start/end timestamps. Returns null for a null/empty/unparseable range (e.g.
// an unscheduled attendee row that carries no time range).
export function parseTimeRangeLiteral(
  literal: string | null | undefined,
): TimeRange | null {
  if (!literal) return null
  const match = literal.match(/[[(]"?([^",)]+)"?,"?([^",)]+)"?[)\]]/)
  if (!match) return null
  const startIso = pgTimestampToIso(match[1])
  const endIso = pgTimestampToIso(match[2])
  if (!startIso || !endIso) return null
  const start = dayjs(startIso)
  const end = dayjs(endIso)
  if (!start.isValid() || !end.isValid()) return null
  return { start: start.toISOString(), end: end.toISOString() }
}

// Attendee statuses that mean the person is NOT doing the visit, so they must
// be excluded everywhere availability/occupancy is derived (hours, revenue,
// utilization aggregations, and the booking slot finder). Single source of
// truth shared across stats, profitability and visit-availability.
export const EXCLUDED_ATTENDEE_STATUSES = ['declined', 'cancelled'] as const

function isExcludedAttendeeStatus(status: string | null | undefined): boolean {
  return (
    status != null &&
    (EXCLUDED_ATTENDEE_STATUSES as readonly string[]).includes(status)
  )
}

// Attach parsed ISO start/end to attendee-join rows carrying a tstzrange
// `range` literal (one row per booked day for multi-day jobs), dropping rows
// whose range is null/invalid. The DB triggers (inherit_visit_times_to_attendee
// / propagate_visit_times_to_attendees) guarantee every attendee of a scheduled
// visit carries its own range — a null range means the visit itself is
// unscheduled (no start/end), so the row has no busy time to contribute. Single
// source of truth for the per-day parse/exclude rule shared by stats
// aggregation and visit-availability, so it can't drift between them.
export function withParsedRange<T extends { range: string | null }>(
  rows: T[],
): (Omit<T, 'range'> & { start: string; end: string })[] {
  return rows.flatMap(({ range, ...rest }) => {
    const parsed = parseTimeRangeLiteral(range)
    if (!parsed) return []
    return [{ ...rest, start: parsed.start, end: parsed.end }]
  })
}

// Minimal structural shape needed to derive a visit's work-day ranges. Both
// VisitWithRelations and lighter visit projections satisfy it.
interface VisitRangeAttendee {
  userId: number
  status?: string | null
  visitTimeRange?: string | null
}
interface VisitRangeSource {
  startTime?: string | null
  endTime?: string | null
  status?: string | null
  visitAttendees?: VisitRangeAttendee[] | null
}

// A visit_attendee whose booking still counts — the inverse of
// EXCLUDED_ATTENDEE_STATUSES. Cancelled/declined attendees neither render a
// work-day segment nor place the visit in that person's row, so both the range
// layer and the timeline's row-membership logic key off this. Shares the same
// single source of truth as stats/profitability/availability.
export const isActiveAttendee = (a: { status?: string | null }): boolean =>
  !isExcludedAttendeeStatus(a.status)

// The distinct work-day ranges a visit covers, derived from its visit_attendees
// rows. Each entry is a (start, end) span. Single-day visits collapse to one
// range (their attendees all inherit the visit span via the DB trigger);
// multi-day jobs yield one range per booked day. Declined attendees on an
// otherwise-active visit are ignored.
//
// A cancelled VISIT is the exception: the backend cascades the cancellation to
// every attendee row (router afterUpdate frees the slot), so filtering by
// isActiveAttendee would drop the whole visit. Its attendee rows still describe
// who was booked on which day, so we keep them — the calendar renders the visit
// struck-through on the right employee/day. This only affects display helpers;
// stats/availability key off isActiveAttendee/withParsedRange directly and are
// unaffected.
//
// - `userId` restricts to one person's bookings (used by per-employee views).
// - `fallbackToVisitSpan` returns the visit's own [startTime, endTime] when no
//   attendee carries an explicit range (e.g. attendee-less or freshly-created
//   visits). Skipped when the visit has no times (unscheduled).
export function getVisitWorkRanges(
  visit: VisitRangeSource,
  opts: { userId?: number; fallbackToVisitSpan?: boolean } = {},
): TimeRange[] {
  const { userId, fallbackToVisitSpan = false } = opts
  const keepCancelledAttendees = visit.status === 'cancelled'
  const ranges = new Map<string, TimeRange>()
  for (const attendee of visit.visitAttendees ?? []) {
    if (!keepCancelledAttendees && !isActiveAttendee(attendee)) continue
    if (userId !== undefined && attendee.userId !== userId) continue
    const bounds = parseTimeRangeLiteral(attendee.visitTimeRange)
    if (bounds) ranges.set(`${bounds.start}|${bounds.end}`, bounds)
  }

  if (!ranges.size) {
    if (fallbackToVisitSpan && visit.startTime && visit.endTime) {
      return [{ start: visit.startTime, end: visit.endTime }]
    }
    return []
  }

  return sortBy(Array.from(ranges.values()), (r) => r.start)
}

// A renderable work-day booking: a (start, end) range carrying the source visit
// so callers can draw a calendar block straight from it. Generic over the visit
// shape, so each app keeps its own visit type (VisitWithRelations, ScheduledVisit, …).
export interface VisitDaySegment<V> extends TimeRange {
  visit: V
}

// Expand a visit into its work-day segments — one per distinct day the
// (selected) attendees are booked — each tagged with the visit. This is the
// shared building block behind the ERP timeline, the employee day list and the
// resident card, so the day-fan-out logic lives in one place.
//
// `fallbackToVisitSpan` defaults to true for the combined view (no `userId`) and
// false when scoped to one person, matching how the per-employee views behave.
// See getVisitWorkRanges for the underlying semantics.
export function getVisitDaySegments<V extends VisitRangeSource>(
  visit: V,
  opts: { userId?: number; fallbackToVisitSpan?: boolean } = {},
): VisitDaySegment<V>[] {
  const { userId, fallbackToVisitSpan = userId === undefined } = opts
  return getVisitWorkRanges(visit, { userId, fallbackToVisitSpan }).map(
    (r) => ({
      visit,
      start: r.start,
      end: r.end,
    }),
  )
}

// A visit spans more than one work day (multi-day) when its attendees are booked
// across more than one distinct day range.
export function isMultiDayVisit(visit: VisitRangeSource): boolean {
  return getVisitWorkRanges(visit).length > 1
}

// Do two spans overlap? Both are treated as half-open [start, end), matching
// how the ranges are stored (tstzrange defaults to '[)'): back-to-back blocks
// don't overlap, and a shift ending at midnight stays on the day it started
// instead of also landing on the next one.
export function rangeOverlaps(a: TimeRange, b: TimeRange): boolean {
  return dayjs(a.start).isBefore(b.end) && dayjs(a.end).isAfter(b.start)
}
